import { Config } from '../config';
import firebase from 'firebase';
import NetworkService from './networkService';

// Use main API as gateway to Stripe services
const API_BASE_URL = Config.Env.ApiBaseUrl;
const networkService = new NetworkService();

export interface StripeConnectStatus {
  isConnected: boolean;
  accountId?: string;
  detailsSubmitted?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
}

interface CreateAccountLinkResponse {
  url: string;
  expiresAt: number;
}

class StripeConnectService {
  /**
   * Creates a Stripe Connect account link for onboarding
   * The main server will forward this request to the Stripe server
   * @returns Promise with the account link URL
   */
  async createAccountLink(): Promise<CreateAccountLinkResponse> {
    try {
      // TODO: Retrieving the token shouldn't be done here. At least I don't believe so.
      const token = await firebase.auth().currentUser?.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/stripe-connect/create-account-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          // The main server can add return/refresh URLs based on environment
          returnUrl: `${window.location.origin}/appointments?stripe_connected=true`,
          refreshUrl: `${window.location.origin}/appointments?stripe_connected=true`
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create account link');
      }

      const responseJson = await response.json();
      console.log('Stripe Connect account link created:', responseJson);
      return responseJson.data;
    } catch (error) {
      console.error('Error creating Stripe Connect account link:', error);
      throw error;
    }
  }

  /**
   * Gets the current Stripe Connect status for the logged-in user
   * The main server will forward this request to the Stripe server
   * @returns Promise with the Stripe Connect status
   */
  async getStripeConnectStatus(): Promise<StripeConnectStatus> {
    try {
      const token = await firebase.auth().currentUser?.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/stripe-connect/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get Stripe Connect status');
      }

      const responseJson = await response.json();
      return responseJson.data ?? responseJson
    } catch (error) {
      console.error('Error getting Stripe Connect status:', error);
      throw error;
    }
  }

  /**
   * Creates a Stripe dashboard link for connected accounts
   * The main server will forward this request to the Stripe server
   * @returns Promise with the dashboard URL
   */
  async createDashboardLink(): Promise<{ url: string }> {
    try {
      const token = await firebase.auth().currentUser?.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/stripe-connect/create-dashboard-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create dashboard link');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating Stripe dashboard link:', error);
      throw error;
    }
  }

  /**
   * Gets the Stripe Connect login link for the logged-in user
   * The main server will forward this request to the Stripe server
   * @returns Promise with the login link URL
   */
  async getLoginLink(): Promise<{ loginLink: string } | null> {
    try {
      const token = await firebase.auth().currentUser?.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/stripe-connect/login-link`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get login link');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error getting Stripe Connect login link:', error);
      throw error;
    }
  }

  /**
   * Creates an instant payout
   * @returns Promise with payoutAmount and status
   */
  async createInstantPayout(): Promise<{ payoutAmount: number; status: string }> {
    try {
      const response = await networkService.post('/stripe-connect/instant-payout');
      return response.data;
    } catch (error) {
      console.error('Error creating instant payout:', error);
      throw error;
    }
  }

  /**
   * Gets the available instant payout balance
   * @returns Promise with instantAvailable amount in cents
   */
  async getInstantPayoutAvailable(): Promise<{ instantAvailable: number }> {
    try {
      const response = await networkService.get('/stripe-connect/instant-payout-available');
      return response.data;
    } catch (error) {
      console.error('Error getting instant payout availability:', error);
      throw error;
    }
  }
}

export default new StripeConnectService();